import { Button, Text } from '@components/ui';
import { ApiUrl } from '@config/apiUrl';
import apiProtected from '@lib/api/apiClient';
import { useAddTipMutation } from '@lib/api/jobs';
import { logError } from '@lib/logError';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import type { Dropin } from 'braintree-web-drop-in';
import DropIn from 'braintree-web-drop-in-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import type { ApiEnvelope, TipPaymentState } from './tipPaymentTypes';


const TipPayment = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as TipPaymentState;
  const tip = state.tip;
  const jobId = state.jobId;

  const [loading, setLoading] = useState(false);
  const addTip = useAddTipMutation();
  const [clientToken, setClientToken] = useState<string | null>(null);
  const [instance, setInstance] = useState<Dropin | null>(null);

  useEffect(() => {
    (async () => {
      const res = await apiProtected.get<ApiEnvelope<string>>(
        `${ApiUrl.server_url}v1/braintree/get-client-token`
      );
      setClientToken(res.data.data ?? null);
    })();
  }, []);

  const handleSubmitPayment = async (): Promise<void> => {
    if (!instance) return;
    setLoading(true);
    const { nonce } = await instance.requestPaymentMethod();
    try {
      const createJobRes = await addTip.mutateAsync({
        tip,
        jobId,
        nonce,
      });
      setLoading(false);
      toast.success(createJobRes.data.message ?? '');
      navigate('/');
    } catch (error: unknown) {
      setLoading(false);
      logError('tip payment', error);
      const errorMassage =
        typeof error === 'object' && error !== null && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : undefined;
      toast.error(errorMassage ?? '');
    }
  };

  if (!clientToken) {
    return (
      <Backdrop
        sx={{
          color: 'text.primary',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={!clientToken}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <>
      <Grid container sx={{ mt: 2 }}>
        <Grid item md={2} lg={2} />
        <Grid item xs={12} sm={12} md={8} lg={8}>
          <Text variant="subtitle" sx={{ fontSize: 18, fontWeight: 700 }}>
            {t('hire.paymentInformation')}
          </Text>
          <Divider sx={{ mt: 1 }} />
        </Grid>
        <Grid item md={2} lg={2} />
      </Grid>
      <Grid container>
        <Grid item md={2} lg={2} />
        <Grid item xs={12} sm={12} md={8} lg={8}>
          <Box sx={{ mt: '50px' }}>
            <DropIn
              options={{
                authorization: clientToken,
                vaultManager: true,
              }}
              onInstance={(dropInInstance) => {
                setInstance(dropInInstance);
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="primary"
                sx={{
                  width: '25%',
                  minWidth: 180,
                  borderRadius: '0px 15px 0px 15px',
                  height: 50,
                }}
                onClick={() => {
                  void handleSubmitPayment();
                }}
              >
                {t('hire.submitPayment')}
              </Button>
            </Box>
          </Box>
        </Grid>
        <Grid item md={2} lg={2} />
      </Grid>
      <Backdrop
        sx={{
          color: 'text.primary',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default TipPayment;
